import React, { useState } from 'react';
import {
  Box,
  Card,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress,
  alpha,
  useTheme,
  Tooltip,
} from '@mui/material';
import {
  Search,
  Refresh,
  FileDownload,
  Warning,
  CheckCircle,
  Error,
  TrendingUp,
  TrendingDown,
  Inventory2,
} from '@mui/icons-material';
import LoadingState from '../../components/common/LoadingState';
import ErrorAlert from '../../components/common/ErrorAlert';
import EmptyState from '../../components/common/EmptyState';
import SuccessSnackbar from '../../components/common/SuccessSnackbar';
import { simpleApi, Product } from '../../services/simpleApi';

interface InventoryItem {
  id: string;
  sku: string;
  name: string;
  category: string;
  currentStock: number;
  reorderPoint: number;
  maxStock: number;
  unit: string;
  location: string;
  status: 'healthy' | 'low' | 'critical' | 'overstock';
  lastRestocked: string;
  supplier: string;
  unitCost: number;
}

const InventoryPage: React.FC = () => {
  const theme = useTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const [inventory, setInventory] = useState<InventoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const fetchItems = async () => {
    try {
      setLoading(true);
      const response = await simpleApi.products.list();
      // Map products to inventory items
      const items: InventoryItem[] = response.items.map((p: Product) => ({
        id: p.id,
        sku: p.sku,
        name: p.name,
        category: p.category || 'Uncategorized',
        currentStock: p.stock,
        reorderPoint: p.reorder_point,
        maxStock: Math.max(p.stock * 2, p.reorder_point * 3),
        unit: 'units',
        location: 'Main Warehouse',
        status: p.stock <= 0 ? 'critical' : p.stock < p.reorder_point ? 'low' : p.stock > p.reorder_point * 3 ? 'overstock' : 'healthy',
        lastRestocked: p.updated_at,
        supplier: 'Primary Supplier',
        unitCost: p.cost,
      }));
      setInventory(items);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to load inventory');
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    fetchItems();
  }, []);

  const clearSuccess = () => setSuccess(null);

  const filteredInventory = (inventory || []).filter(item =>
    item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.sku.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.category.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'healthy':
        return { color: 'success', icon: <CheckCircle />, label: 'Healthy' };
      case 'low':
        return { color: 'warning', icon: <Warning />, label: 'Low Stock' };
      case 'critical':
        return { color: 'error', icon: <Error />, label: 'Critical' };
      case 'overstock':
        return { color: 'info', icon: <TrendingUp />, label: 'Overstock' };
      default:
        return { color: 'default', icon: null, label: status };
    }
  };

  const getStockPercentage = (current: number, max: number) => {
    return (current / max) * 100;
  };

  const getStockColor = (status: string) => {
    switch (status) {
      case 'healthy': return theme.palette.success.main;
      case 'low': return theme.palette.warning.main;
      case 'critical': return theme.palette.error.main;
      case 'overstock': return theme.palette.info.main;
      default: return theme.palette.grey[500];
    }
  };

  const totalValue = inventory.reduce((sum, item) => sum + (item.currentStock * item.unitCost), 0);
  const criticalItems = inventory.filter(item => item.status === 'critical').length;
  const lowStockItems = inventory.filter(item => item.status === 'low').length;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            Inventory Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Monitor stock levels and manage inventory
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="outlined" startIcon={<Refresh />}>
            Sync
          </Button>
          <Button variant="outlined" startIcon={<FileDownload />}>
            Export
          </Button>
        </Box>
      </Box>

      {error && <ErrorAlert error={error} onRetry={fetchItems} />}

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <Box sx={{ p: 2 }}>
              <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
                {inventory.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Items
              </Typography>
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <Box sx={{ p: 2 }}>
              <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.error.main }}>
                {criticalItems}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Critical Stock
              </Typography>
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <Box sx={{ p: 2 }}>
              <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.warning.main }}>
                {lowStockItems}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Low Stock
              </Typography>
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <Box sx={{ p: 2 }}>
              <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.success.main }}>
                £{totalValue.toLocaleString()}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Value
              </Typography>
            </Box>
          </Card>
        </Grid>
      </Grid>

      {/* Search and Filters */}
      <Card sx={{ mb: 3, p: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="Search inventory..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select label="Category" defaultValue="all">
                <MenuItem value="all">All Categories</MenuItem>
                <MenuItem value="electronics">Electronics</MenuItem>
                <MenuItem value="accessories">Accessories</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select label="Status" defaultValue="all">
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="healthy">Healthy</MenuItem>
                <MenuItem value="low">Low Stock</MenuItem>
                <MenuItem value="critical">Critical</MenuItem>
                <MenuItem value="overstock">Overstock</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Card>

      {/* Inventory Table */}
      <Card>
        {loading ? (
          <LoadingState message="Loading inventory..." />
        ) : filteredInventory.length === 0 ? (
          <EmptyState
            title="No inventory items found"
            description="Start by adding your first inventory item"
            actionLabel="Add Item"
            icon={<Inventory2 sx={{ fontSize: 64, color: 'text.disabled' }} />}
          />
        ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Item</TableCell>
                <TableCell>Location</TableCell>
                <TableCell>Current Stock</TableCell>
                <TableCell>Stock Level</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Supplier</TableCell>
                <TableCell align="right">Unit Cost</TableCell>
                <TableCell align="right">Total Value</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredInventory.map((item) => {
                const statusConfig = getStatusConfig(item.status);
                const stockPercentage = getStockPercentage(item.currentStock, item.maxStock);
                const stockColor = getStockColor(item.status);
                
                return (
                  <TableRow key={item.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Inventory2 color="action" />
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {item.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            SKU: {item.sku}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>{item.location}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {item.currentStock} {item.unit}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Reorder: {item.reorderPoint}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ width: 100 }}>
                        <LinearProgress
                          variant="determinate"
                          value={stockPercentage}
                          sx={{
                            height: 8,
                            borderRadius: 4,
                            bgcolor: alpha(stockColor, 0.1),
                            '& .MuiLinearProgress-bar': {
                              bgcolor: stockColor,
                            },
                          }}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                          {stockPercentage.toFixed(0)}% of max
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={statusConfig.icon || undefined}
                        label={statusConfig.label}
                        size="small"
                        color={statusConfig.color as any}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{item.supplier}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      £{item.unitCost.toFixed(2)}
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        £{(item.currentStock * item.unitCost).toLocaleString()}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        )}
      </Card>

      <SuccessSnackbar
        open={!!success}
        message={success || ''}
        onClose={clearSuccess}
      />

      {/* Alerts Section */}
      {(criticalItems > 0 || lowStockItems > 0) && (
        <Card sx={{ mt: 3, p: 2, bgcolor: alpha(theme.palette.warning.main, 0.05), border: 1, borderColor: theme.palette.warning.main }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Warning sx={{ color: theme.palette.warning.main, fontSize: 32 }} />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Stock Alerts
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {criticalItems} critical items and {lowStockItems} low stock items need attention
              </Typography>
            </Box>
            <Button variant="contained" color="warning" sx={{ ml: 'auto' }}>
              Create Reorder
            </Button>
          </Box>
        </Card>
      )}
    </Box>
  );
};

export default InventoryPage;
